import type { PaymentGrid } from '@makemydeal/dr-platform-shared';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { FINANCE, RESIDUAL_DOLLAR } from '@makemydeal/dr-platform-types';
import { getOfferDetails } from '../selectors/offerNodeSelectors';
import { getPaymentGridOverriddenInputIndices, isAspPaymentGridRedesignActive } from '../selectors/paymentGridSelectors';
import { getAllTermsFromOffer } from '../selectors/slices/offerSliceSelectors';
import { getTermIdIdWithExtension } from '../selectors/utils/nonSelectorUtils';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';
import type { GridPaymentGridData } from '../types/paymentTypes';

export type ScenarioPaymentGridInput = {
    offerType?: OfferType | string;
    penciledInputs?: Record<string, any>;
    paymentData?: Record<string, any>;
};

export type PaymentGridSaveOptions = { isOverrideIndicatorEnabled?: boolean };

export const getPaymentGridSaveData = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    { isOverrideIndicatorEnabled = false }: PaymentGridSaveOptions = {}
): GridPaymentGridData | undefined => {
    const paymentGridData = getOfferDetails(state, offerType).paymentGridData as GridPaymentGridData | undefined;
    if (!paymentGridData?.paymentGridToggle) return undefined;

    const gridTerms = (paymentGridData.terms || []) as (number | null)[];
    const hasDuplicateCashDown = (paymentGridData.cashDown ?? []).some(
        (cash, index, all) => cash != null && all.indexOf(cash) !== index
    );
    const duplicatedColumns = gridTerms.reduce<number[]>((positions, term, columnIndex) => {
        if (term == null) return positions;
        if (hasDuplicateCashDown || gridTerms.filter((other) => other === term).length > 1) positions.push(columnIndex);
        return positions;
    }, []);

    const pickForDuplicates = <T>(byColumn: Record<number, T> | undefined) => {
        if (!byColumn || !duplicatedColumns.length) return undefined;
        const picked = duplicatedColumns.reduce<Record<number, T>>((entries, columnIndex) => {
            if (byColumn[columnIndex] !== undefined) entries[columnIndex] = byColumn[columnIndex];
            return entries;
        }, {});
        return Object.keys(picked).length ? picked : undefined;
    };

    const columnOverrides = pickForDuplicates(paymentGridData.columnOverrides);
    const savedOverriddenInputs =
        isOverrideIndicatorEnabled && isAspPaymentGridRedesignActive(state, offerType)
            ? getPaymentGridOverriddenInputIndices(state, offerType)
            : undefined;
    const columnCells = paymentGridData.columnCells
        ? Object.entries(paymentGridData.columnCells as Record<string, Record<string, any>>).reduce<
              Record<number, Record<number, any>>
          >((cells, [column, rows]) => {
              const columnIndex = Number(column);
              if (!duplicatedColumns.includes(columnIndex)) return cells;
              cells[columnIndex] = { ...(rows || {}) };
              return cells;
          }, {})
        : undefined;

    return {
        terms: paymentGridData.terms,
        cashDown: paymentGridData.cashDown,
        ...(columnOverrides && { columnOverrides }),
        ...(columnCells && Object.keys(columnCells).length && { columnCells }),
        ...(savedOverriddenInputs && Object.keys(savedOverriddenInputs).length && { savedOverriddenInputs }),
        /**
         * The only record of the active cell. The bare indices that used to sit here had no reader left; the
         * compressed baggage still emits them for Sales View, derived from this stamp.
         */
        ...(paymentGridData.activeCell && { activeCell: paymentGridData.activeCell })
    } as GridPaymentGridData;
};

export const getScenarioPaymentGridInput = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    options: PaymentGridSaveOptions = {}
): ScenarioPaymentGridInput => {
    const inputs = getOfferDetails(state, offerType);
    const paymentGridData = getPaymentGridSaveData(state, offerType, options);
    return {
        offerType,
        penciledInputs: {
            [offerType]: {
                term: inputs.term,
                overridesByTerm: inputs.overridesByTerm,
                ...(paymentGridData && { paymentGridData })
            }
        },
        paymentData: getAllTermsFromOffer(state.offer)
    };
};

/**
 * Term id for one grid cell — the canonical helper, not a local copy.
 *
 * `getTermIdIdWithExtension` is what writes the keys in `paymentData` (`paymentGridSelectors` keys grid
 * cells through the same function), so using it is what guarantees a lookup here finds its priced
 * response. It also owns the two rules a hand-rolled version kept getting wrong: `sanitizeTermId`
 * replaces **every** dot, and `isExtensionValueValid` drops a zero extension, so a zero-cash-down row
 * carries no `_downpayment_` suffix.
 *
 * `termIdWithExtention: true` is passed explicitly rather than left to `IS_TERMID_WITH_DOWNPAYMENT`.
 * Manager View sets that config to true, so the resolved behaviour is the same — but a stored grid keyed
 * off another library's runtime config would silently key every row identically if it were ever unset,
 * collapsing the cash-down axis. The one selector that already needs this guarantee
 * (`getTermIdForOfferTypeAndTermMonth`) forces the same flag for the same reason.
 */
export const buildTermId = (offerType: string, term: number, dueAtSigning: number): string | undefined =>
    getTermIdIdWithExtension(offerType as OfferType, term, dueAtSigning, {
        getPairs: true,
        termIdWithExtention: true
    });

/**
 * Payment Services sends `''` rather than `null` for an absent percent residual, which `??` would
 * happily persist. Mirrors the save path's coercion so the two grids agree.
 */
const toNumberOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

/**
 * The complete grid for one scenario — the axes plus, for every cell, the full priced response it was
 * quoted from.
 *
 * `at` is the calculation timestamp the consumer's freshness check reads. It comes from the grid state's
 * per-cell record of the last successful payment response, so a save that re-prices one row revises only
 * those cells and leaves the rest at the time they actually priced.
 *
 * A cell whose payment call failed has no entry in `paymentData` and is stored with `po: null` and no
 * `at` — it carries no quote, so it degrades on its own rather than taking the grid down.
 */
export type PaymentGridBuildOptions = {
    isRedesignEnabled?: boolean;
    isCellCashDownMatchEnabled?: boolean;
};

export const buildScenarioPaymentGrid = (
    input: ScenarioPaymentGridInput,
    cellPricedAt: Record<string, number> = {},
    columnSources: Record<number, string> = {},
    { isRedesignEnabled = false, isCellCashDownMatchEnabled = false }: PaymentGridBuildOptions = {}
): PaymentGrid | undefined => {
    const offerType = input?.offerType;
    if (!offerType) return undefined;

    const offerInputs = input.penciledInputs?.[offerType];
    const paymentGridData = offerInputs?.paymentGridData;
    if (!paymentGridData?.terms?.length || !paymentGridData.cashDown?.length) return undefined;

    const paymentData = (input.paymentData ?? {}) as Record<string, any>;
    const overrides = offerInputs.overridesByTerm ?? {};
    const defaultOverrideField = offerType === FINANCE ? 'aprOverride' : 'mfOverride';

    // Mirrors the save path: an incomplete deal can price nothing, and a grid of empty cells tells a
    // consumer "priced, nothing here" instead of letting it fall back.
    if (!Object.keys(paymentData).length) return undefined;

    const columnOverrides = (paymentGridData as GridPaymentGridData).columnOverrides ?? {};
    const columnCells = (paymentGridData as GridPaymentGridData).columnCells ?? {};
    const decidedColumns = (paymentGridData as GridPaymentGridData).decidedColumns ?? {};
    const savedOverriddenInputs = (paymentGridData as GridPaymentGridData).savedOverriddenInputs;
    const gridTerms = paymentGridData.terms as (number | null)[];
    const stampedActiveCell = (paymentGridData as GridPaymentGridData).activeCell;
    const selectedTermMonths = (paymentData[offerInputs.term] as any)?.months;
    const firstColumnOnSelectedTerm = gridTerms.indexOf(selectedTermMonths);
    const isStampedColumnValid =
        stampedActiveCell?.columnIndex != null &&
        gridTerms[stampedActiveCell.columnIndex] != null &&
        gridTerms[stampedActiveCell.columnIndex] === stampedActiveCell.term;
    const selectedColumnIndex = !isRedesignEnabled
        ? undefined
        : isStampedColumnValid
        ? stampedActiveCell.columnIndex
        : firstColumnOnSelectedTerm >= 0
        ? firstColumnOnSelectedTerm
        : undefined;

    const columnPlan = gridTerms
        .map((term, columnIndex) => ({ term, columnIndex }))
        .filter((entry): entry is { term: number; columnIndex: number } => entry.term != null);

    const columns = columnPlan.map(({ term, columnIndex }) => {
        const ownOverride = columnOverrides[columnIndex] ?? {};
        const termOverride =
            isRedesignEnabled && gridTerms.filter((gridTerm) => gridTerm === term).length > 1 ? undefined : overrides[term];
        const sellRateOverride = ownOverride.sellRate ?? termOverride?.sellRate;
        const sellRateUnit =
            (ownOverride.sellRate != null ? ownOverride.rateType : termOverride?.rateType) ??
            (isRedesignEnabled ? offerInputs.rateTypeOverride : undefined);
        const overrideField =
            sellRateUnit === 'APR' ? 'aprOverride' : sellRateUnit === 'MoneyFactor' ? 'mfOverride' : defaultOverrideField;
        const residualInfoOverride = ownOverride.residualInfo ?? termOverride?.residualInfo;
        const isDollarResidual = residualInfoOverride?.baseResidualType === RESIDUAL_DOLLAR;
        const residualOverride = isDollarResidual
            ? residualInfoOverride.programResidual
            : residualInfoOverride?.programResidualPercent;
        const residualOverrideField = isDollarResidual ? 'residualDollarOverride' : 'residualPercentOverride';
        const lenderProgram = Object.values(paymentData).find((entry: any) => entry?.months === term)?.lenderProgram;
        const shouldSaveLenderProgram = Boolean(lenderProgram?.programName);
        return {
            term,
            ...((decidedColumns[columnIndex] ?? columnSources[term]) && {
                source: decidedColumns[columnIndex] ?? columnSources[term]
            }),
            ...(sellRateOverride != null && { [overrideField]: sellRateOverride }),
            ...(residualOverride != null && { [residualOverrideField]: residualOverride }),
            ...(sellRateOverride != null && savedOverriddenInputs?.sellRate?.includes(columnIndex) && { sellRateOverridden: true }),
            ...(residualOverride != null && savedOverriddenInputs?.residual?.includes(columnIndex) && { residualOverridden: true }),
            ...(shouldSaveLenderProgram && {
                lenderProgram: {
                    lenderId: lenderProgram.lenderId,
                    branchId: lenderProgram.branchId,
                    programType: lenderProgram.programType,
                    programName: lenderProgram.programName,
                    regionalId: lenderProgram.regionalId,
                    rateProgram: lenderProgram.rateProgram
                }
            })
        };
    });

    const rows = (paymentGridData.cashDown as (number | null)[])
        .filter((dueAtSigning): dueAtSigning is number => dueAtSigning != null)
        .map((dueAtSigning) => ({ dueAtSigning }));

    if (!rows.length || !columns.length) return undefined;

    const stampedRowIndex =
        stampedActiveCell?.rowIndex != null &&
        rows[stampedActiveCell.rowIndex]?.dueAtSigning != null &&
        rows[stampedActiveCell.rowIndex].dueAtSigning === stampedActiveCell.downPayment
            ? stampedActiveCell.rowIndex
            : undefined;
    const narrowingRow = isRedesignEnabled ? stampedRowIndex : undefined;

    const cells = rows.map(({ dueAtSigning }, rowIndex) =>
        columnPlan.map(({ term, columnIndex }): PaymentGrid['cells'][number][number] => {
            const termId = buildTermId(offerType, term, dueAtSigning);
            const ownRowCell = columnCells[columnIndex]?.[rowIndex];
            const isOwnCellForRowCashDown =
                !isCellCashDownMatchEnabled || ownRowCell?.downPayment == null || ownRowCell.downPayment === dueAtSigning;
            const ownCell =
                ownRowCell && (ownRowCell.term == null || ownRowCell.term === term) && isOwnCellForRowCashDown
                    ? ownRowCell
                    : undefined;
            // No term id means no way to find this cell's quote — store it unpriced rather than guessing.
            const termInfo = ownCell ?? (termId ? paymentData[termId] : undefined);
            const isSelected =
                Boolean(termId) &&
                termId === offerInputs.term &&
                (selectedColumnIndex === undefined || columnIndex === selectedColumnIndex) &&
                (narrowingRow === undefined || rowIndex === narrowingRow);

            if (!termInfo) {
                return { payment: null, lenderCode: null, sellRate: null, rateType: null, residual: null, isSelected, po: null };
            }

            // The cell's own priced-at, never a save-time stamp: `at` is the freshness clock, so a cell
            // that was not re-priced has to keep the timestamp it already had. A cell with no recorded
            // time is stored without one and is excluded from oldestAt.
            const at = cellPricedAt[termId as string];

            return {
                payment: termInfo.monthlyPayment,
                lenderCode: termInfo.lenderCode,
                sellRate: termInfo.sellRate,
                rateType: termInfo.rateType ?? null,
                // Sales View hydrates only the active term into terms.byId, so the priced residual for
                // every other cell has to come off the baggage the same way payment does.
                residual: toNumberOrNull(termInfo.residualPercentage) ?? toNumberOrNull(termInfo.baseResidualDollar),
                isSelected,
                po: termInfo,
                ...(at !== undefined && { at })
            };
        })
    );

    const dealRecord = paymentData[offerInputs.term] as any;
    const dealDownPayment = dealRecord?.requestedDownPayment ?? dealRecord?.dueAtSigning ?? dealRecord?.downPayment;
    const selectedRowIndex = !isRedesignEnabled
        ? undefined
        : stampedRowIndex ??
          (dealDownPayment != null
              ? rows.findIndex(({ dueAtSigning }) => dueAtSigning === dealDownPayment)
              : rows.findIndex(
                    ({ dueAtSigning }) => buildTermId(offerType, selectedTermMonths, dueAtSigning) === offerInputs.term
                ));

    return {
        rows,
        columns,
        cells,
        ...(selectedColumnIndex != null && selectedColumnIndex >= 0 && { selectedColumnIndex }),
        ...(selectedRowIndex != null && selectedRowIndex >= 0 && { selectedRowIndex })
    };
};
